import React, { useMemo } from 'react';
import { ClockIcon } from '@heroicons/react/24/outline';
import { getResourceTimezoneLabel } from '../../utils/datetime';

const TZ_LABEL = getResourceTimezoneLabel();

const formatHour = (h) => `${String(h).padStart(2, '0')}:00`;

// Absolute rank tier — meaningful across slices because rank is a position.
const tierForRank = (rank) => {
  if (rank == null) return 'empty';
  if (rank <= 5) return 'top';
  if (rank <= 10) return 'mid';
  return 'tail';
};

// Relative percentile tier — used in errors mode where absolute thresholds
// don't translate across resources. Best third → top, middle → mid, worst → tail.
// Collapses to neutral 'mid' when there's no spread or fewer than 3 gates.
const tierForErrorPercentile = (value, sortedValues) => {
  if (value == null || !sortedValues.length) return 'empty';
  if (sortedValues.length < 3) return 'mid';
  if (sortedValues[0] === sortedValues[sortedValues.length - 1]) return 'mid';
  const idx = sortedValues.indexOf(value);
  const third = sortedValues.length / 3;
  if (idx < third) return 'top';
  if (idx < third * 2) return 'mid';
  return 'tail';
};

// Flatter tier system: cells share the card surface, separated by a thin
// canonical border. Color comes from the value text + a small dot — not from
// stacked background tints. This keeps the grid quiet when ranks are close.
const TIER = {
  top: {
    text:  'text-emerald-700 dark:text-emerald-300',
    accent:'bg-emerald-500 dark:bg-emerald-400',
  },
  mid: {
    text:  'text-brand-700 dark:text-brand-300',
    accent:'bg-brand-500 dark:bg-brand-400',
  },
  tail: {
    text:  'text-accent-600 dark:text-accent-400',
    accent:'bg-accent-500 dark:bg-accent-400',
  },
  empty: {
    text:  'text-gray-400 dark:text-gray-500',
    accent:'bg-gray-300 dark:bg-gray-600',
  },
};

/**
 * Gate-closure performance — a compact 24-hour heatmap of submission-hour
 * outcomes, anchored by side-by-side Best/Worst callout tiles.
 *
 * Replaces the prior 24-row horizontal-bar layout, which scaled poorly
 * vertically and provided weak visual differentiation when ranks were close.
 *
 * Modes:
 *   - rankings: cell tier from absolute rank (Top 5 / 6–10 / outside).
 *   - errors:   cell tier from relative percentile within the slice
 *               (best third / mid / worst third). All-equal collapses to
 *               neutral mid since absolute error thresholds aren't portable.
 *
 * @param {{
 *   mode?: 'rankings' | 'errors',
 *   gateClosureData?: Array<{
 *     gate_closure_hour: number,
 *     avg_rank?: number,
 *     avg_score_w_pen?: number,
 *     avg_score?: number,
 *     nr_participants?: number,
 *     days_with_submissions?: number,
 *   }>,
 *   loading?: boolean,
 *   totalForecasters?: number,
 *   metricLabel?: string,
 *   className?: string,
 * }} props
 */
export default function GateClosurePerformanceCard({
  mode = 'rankings',
  gateClosureData = [],
  loading = false,
  totalForecasters = null,
  metricLabel = '',
  className = '',
}) {
  const isRankings = mode === 'rankings';
  const valueOf = (r) =>
    isRankings ? r.avg_rank : (r.avg_score_w_pen ?? r.avg_score);

  const rows = useMemo(() => {
    if (!Array.isArray(gateClosureData)) return [];
    return [...gateClosureData]
      .filter((r) => r && typeof r.gate_closure_hour === 'number')
      .sort((a, b) => a.gate_closure_hour - b.gate_closure_hour);
  }, [gateClosureData]);

  const { valid, best, worst, sortedErrorValues } = useMemo(() => {
    const v = rows.filter((r) => valueOf(r) != null);
    const b = v.reduce(
      (acc, r) => (!acc || valueOf(r) < valueOf(acc) ? r : acc),
      null,
    );
    const w = v.reduce(
      (acc, r) => (!acc || valueOf(r) > valueOf(acc) ? r : acc),
      null,
    );
    const sortedErr = isRankings ? [] : v.map(valueOf).sort((x, y) => x - y);
    return { valid: v, best: b, worst: w, sortedErrorValues: sortedErr };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, isRankings]);

  const sameBestWorst =
    !!best && !!worst && best.gate_closure_hour === worst.gate_closure_hour;

  const formatValue = (v) =>
    isRankings ? `#${Number(v).toFixed(1)}` : Number(v).toFixed(2);

  const headerContent = (
    <header className="mb-4">
      <div className="text-[11px] uppercase tracking-[0.22em] font-medium text-brand-600 dark:text-brand-400">
        Gate Closure Performance
      </div>
      <h3 className="mt-1.5 text-base font-semibold tracking-tight text-slate-900 dark:text-white">
        When you submit matters
      </h3>
      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
        {isRankings
          ? 'Average rank per submission hour. Lower rank wins.'
          : `Average ${metricLabel || 'error'} per submission hour. Lower error wins.`}
      </p>
    </header>
  );

  const CARD_SHELL =
    'bg-surface dark:bg-gray-870 rounded-xl p-6 shadow-sm ' +
    'border border-gray-200/70 dark:border-gray-600/70';

  if (loading) {
    return (
      <section className={className}>
        {headerContent}
        <div className={CARD_SHELL}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-px mb-6 overflow-hidden rounded-lg bg-slate-200 dark:bg-gray-700">
            <div className="h-[88px] bg-gray-100 dark:bg-gray-700/40 animate-pulse" />
            <div className="h-[88px] bg-gray-100 dark:bg-gray-700/40 animate-pulse" />
          </div>
          <div className="overflow-hidden rounded-lg">
            <div className="grid grid-cols-6 sm:grid-cols-8 lg:grid-cols-12 -ml-px">
              {[...Array(24)].map((_, i) => (
                <div
                  key={i}
                  className="h-[58px] bg-gray-100 dark:bg-gray-700/40 border-l border-slate-200 dark:border-gray-700 animate-pulse"
                />
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (valid.length === 0) {
    return (
      <section className={className}>
        {headerContent}
        <div className={CARD_SHELL}>
          <div className="flex items-start gap-3">
            <ClockIcon className="h-5 w-5 text-gray-400 dark:text-gray-500 flex-shrink-0 mt-0.5" />
            <div>
              <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                No per-gate breakdown for this period
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 max-w-md">
                Per-gate-closure stats become available after at least one closed
                session has been scored for this horizon.
              </p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  const valueSuffix = isRankings ? 'avg rank' : (metricLabel || 'avg error');

  return (
    <section className={className}>
      {headerContent}
      <div className={CARD_SHELL}>
        <style>{`
          @keyframes gcp-cell-in {
            from { opacity: 0; transform: translateY(2px); }
            to   { opacity: 1; transform: translateY(0); }
          }
          .gcp-cell { animation: gcp-cell-in 280ms ease-out backwards; }
        `}</style>

        {/* ── Best / Worst summary ────────────────────────────────────── */}
        {!sameBestWorst && (
          <div className="mb-6 grid grid-cols-1 sm:grid-cols-2 gap-px overflow-hidden rounded-lg bg-slate-200 dark:bg-gray-700">
            <SummaryTile
              kind="best"
              hour={best.gate_closure_hour}
              value={formatValue(valueOf(best))}
              days={best.days_with_submissions}
              valueSuffix={valueSuffix}
            />
            <SummaryTile
              kind="worst"
              hour={worst.gate_closure_hour}
              value={formatValue(valueOf(worst))}
              days={worst.days_with_submissions}
              valueSuffix={valueSuffix}
            />
          </div>
        )}

        {/* ── Hourly heatmap ──────────────────────────────────────────── */}
        <div className="mb-3 flex items-baseline justify-between">
          <div className="text-[11px] uppercase tracking-[0.22em] font-medium text-brand-600 dark:text-brand-400">
            Hourly average
          </div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-gray-400 dark:text-gray-500">
            {TZ_LABEL}
          </div>
        </div>
        <div className="overflow-hidden rounded-lg">
        <div className="grid grid-cols-6 sm:grid-cols-8 lg:grid-cols-12 -ml-px">
          {valid.map((r, i) => {
            const value = valueOf(r);
            const tierKey = isRankings
              ? tierForRank(r.avg_rank)
              : (sameBestWorst ? 'mid' : tierForErrorPercentile(value, sortedErrorValues));
            const t = TIER[tierKey];
            const isBest = !sameBestWorst && r.gate_closure_hour === best?.gate_closure_hour;
            const isWorst = !sameBestWorst && r.gate_closure_hour === worst?.gate_closure_hour;
            const days = r.days_with_submissions;
            const formatted = formatValue(value);

            return (
              <div
                key={r.gate_closure_hour}
                title={`${formatHour(r.gate_closure_hour)} ${TZ_LABEL} · ${formatted}${
                  days != null ? ` · ${days} day${days === 1 ? '' : 's'}` : ''
                }`}
                aria-label={`${formatHour(r.gate_closure_hour)} ${formatted}${
                  isBest ? ' best' : isWorst ? ' worst' : ''
                }`}
                className={[
                  'gcp-cell relative bg-surface dark:bg-gray-870 px-2.5 py-2',
                  'border-l border-slate-200 dark:border-gray-700',
                  'transition-colors duration-150',
                  'hover:bg-slate-900/[0.02] dark:hover:bg-white/[0.02]',
                ].join(' ')}
                style={{ animationDelay: `${i * 12}ms` }}
              >
                {(isBest || isWorst) && (
                  <span
                    aria-hidden="true"
                    className={`absolute left-0 top-0 bottom-0 w-[2px] ${t.accent}`}
                  />
                )}
                <div className="text-[10px] uppercase tracking-[0.12em] font-medium font-mono tabular-nums text-gray-500 dark:text-gray-500">
                  {formatHour(r.gate_closure_hour)}
                </div>
                <div className={`mt-1 text-[17px] font-semibold tracking-tight font-mono tabular-nums leading-none ${t.text}`}>
                  {formatted}
                </div>
                {days != null && (
                  <div className="mt-1 text-[10px] font-mono tabular-nums text-gray-400 dark:text-gray-500">
                    {days}d
                  </div>
                )}
              </div>
            );
          })}
        </div>
        </div>

        {/* ── Footer ──────────────────────────────────────────────────── */}
        <div className="mt-5 pt-4 border-t border-slate-900/10 dark:border-gray-100/10 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-gray-500 dark:text-gray-400">
          {isRankings ? (
            <>
              <Legend dot={TIER.top.accent} label="Top 5" />
              <Legend dot={TIER.mid.accent} label="Top 6–10" />
              <Legend dot={TIER.tail.accent} label="Outside top 10" />
              <span className="ml-auto tabular-nums">
                {valid.length} gate{valid.length === 1 ? '' : 's'}
                {totalForecasters ? ` · of ${totalForecasters} forecasters` : ''}
              </span>
            </>
          ) : (
            <>
              <Legend dot={TIER.top.accent} label="Best third" />
              <Legend dot={TIER.mid.accent} label="Mid third" />
              <Legend dot={TIER.tail.accent} label="Worst third" />
              <span className="ml-auto tabular-nums">
                {valid.length} gate{valid.length === 1 ? '' : 's'}
                {metricLabel ? ` · ${metricLabel}` : ''}
              </span>
            </>
          )}
        </div>
      </div>
    </section>
  );
}

function SummaryTile({ kind, hour, value, days, valueSuffix }) {
  const isBest = kind === 'best';
  const eyebrowCls = isBest
    ? 'text-emerald-600 dark:text-emerald-400'
    : 'text-accent-600 dark:text-accent-400';
  const accentCls = isBest
    ? 'bg-emerald-500 dark:bg-emerald-400'
    : 'bg-accent-500 dark:bg-accent-400';
  const label = isBest ? 'Best gate' : 'Worst gate';

  return (
    <div className="relative bg-surface dark:bg-gray-870 px-5 py-4">
      <span
        aria-hidden="true"
        className={`absolute left-0 top-0 bottom-0 w-[2px] ${accentCls}`}
      />
      <div className={`text-[10px] uppercase tracking-[0.22em] font-medium ${eyebrowCls}`}>
        {label}
      </div>
      <div className="mt-2 flex items-baseline gap-2">
        <span className="text-3xl font-semibold tracking-tight font-mono tabular-nums text-slate-900 dark:text-white leading-none">
          {String(hour).padStart(2, '0')}:00
        </span>
        <span className="text-[10px] uppercase tracking-[0.18em] text-gray-400 dark:text-gray-500">
          {TZ_LABEL}
        </span>
      </div>
      <div className="mt-2 flex items-baseline gap-1.5 text-xs text-gray-500 dark:text-gray-400">
        <span className="font-medium font-mono tabular-nums text-slate-700 dark:text-gray-200">
          {value}
        </span>
        <span>{valueSuffix}</span>
        {days != null && (
          <span className="font-mono tabular-nums text-gray-400 dark:text-gray-500">
            · {days}d
          </span>
        )}
      </div>
    </div>
  );
}

function Legend({ dot, label }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className={`h-2 w-2 rounded-full ${dot}`} />
      {label}
    </div>
  );
}
